import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';

export interface StatTileCountUpOptions {
  decimalPlaces: number;
  duration     : number;
  startVal     : number;
  useEasing    : boolean;
}

@Component({
  selector: 'app-stat-tile',
  templateUrl: './stat-tile.component.html',
  styleUrls: ['./stat-tile.component.scss']
})
export class StatTileComponent implements OnChanges {

  @Input() accentColour    : string='primary';
  @Input() caption         : string | null | undefined;
  @Input() decimalPlaces   : number=0;
  @Input() hint            : string | null | undefined;
  @Input() hintTooltipClass: string='';
  @Input() icon            : string | null | undefined;
  @Input() label           : string='';
  @Input() link            : string | any[] | null | undefined;
  @Input() loading         : boolean=false;
  @Input() note            : string | null | undefined;
  @Input() prefix          : string | null | undefined;
  @Input() selectable      : boolean=false;
  @Input() selected        : boolean=false;
  @Input() selectedHint    : string='Selected - click again to deselect';
  @Input() suffix          : string | null | undefined;
  @Input() trendPercent    : number | null | undefined;
  @Input() trendUpIsGood   : boolean=true;
  @Input() useCountUp      : boolean=true;
  @Input() value           : number | string | null | undefined;

  @Output() tileClick: EventEmitter<void>=new EventEmitter<void>();

  public countUpOptions: StatTileCountUpOptions={ decimalPlaces: 0, duration: 2, startVal: 0, useEasing: true };

  constructor(
    private exceptionsService: ExceptionsService
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['decimalPlaces'])
        this.countUpOptions={ ...this.countUpOptions, decimalPlaces: Math.max(0, this.decimalPlaces ?? 0) };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Public

  public accentVar(): string {
    try {
      const colour=(this.accentColour || 'primary').trim();

      if (/^(#|rgb|hsl|var\()/i.test(colour))
        return colour;

      return 'var(--vz-' + colour + ')';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'var(--vz-primary)';
    }
  }

  public hasFooter(): boolean {
    try {
      return this.hasTrend() || !!this.caption || !!this.note;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public hasTrend(): boolean {
    try {
      return this.trendPercent!=null && !isNaN(this.trendPercent);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public isNumber(): boolean {
    try {
      return typeof this.value==='number' && !isNaN(this.value);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public numberFormat(): string {
    try {
      const dp=Math.max(0, this.decimalPlaces ?? 0);
      return '1.' + dp + '-' + dp;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '1.0-0';
    }
  }

  public numericValue(): number {
    try {
      return this.isNumber() ? <number>this.value : 0;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public onTileClick(event?: Event): void {
    try {
      if (!this.selectable)
        return;

      event?.preventDefault();
      this.tileClick.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public plainLabel(): string {
    try {
      return (this.label ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public trendClass(): string {
    try {
      const change=this.trendPercent ?? 0;

      if (change===0)
        return 'text-muted';

      return (change>0)===this.trendUpIsGood ? 'text-success' : 'text-danger';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'text-muted';
    }
  }

  public trendIcon(): string {
    try {
      const change=this.trendPercent ?? 0;
      return change>0 ? 'ri-arrow-up-s-fill' : change<0 ? 'ri-arrow-down-s-fill' : 'ri-subtract-line';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public trendSize(): number {
    try {
      return Math.abs(this.trendPercent ?? 0);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  //#endregion
}
